import styled from '@emotion/styled';
import { type ToolCall } from '@korvo/core/types/chat';
import React, { useEffect,useRef, useState } from 'react';

type ToolCallMessageProps = {
  toolCalls: ToolCall[];
  content: string;
}

const Container = styled.div`
  background: var(--background-muted);
  border: 1px solid var(--border-default);
  border-radius: 8px;
  padding: 12px;
  margin: 8px 0;
  font-family: 'Segoe UI', system-ui, sans-serif;
  max-width: min(80%, 600px);
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  
  @media (max-width: 768px) {
    max-width: 95%;
    padding: 10px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
`;

const Title = styled.div`
  font-weight: 600;
  color: var(--foreground-default);
  font-size: 14px;
`;

const Status = styled.div<{ success?: boolean }>`
  font-size: 12px;
  color: ${props => props.success ? 'var(--success-default)' : 'var(--danger-default)'};
  font-weight: 500;
`;

const ExpandIcon = styled.div<{ expanded: boolean }>`
  transform: ${props => props.expanded ? 'rotate(180deg)' : 'rotate(0deg)'};
  transition: transform 0.2s ease;
  font-size: 12px;
  color: var(--foreground-muted);
`;

const Details = styled.div<{ expanded: boolean }>`
  max-height: ${props => props.expanded ? '500px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
  margin-top: ${props => props.expanded ? '12px' : '0'};
  
  @media (max-width: 768px) {
    max-height: ${props => props.expanded ? '400px' : '0'};
    margin-top: ${props => props.expanded ? '10px' : '0'};
  }
`;

const ToolCallItem = styled.div`
  background: var(--background-secondary);
  border: 1px solid var(--border-default);
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 8px;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: 768px) {
    padding: 10px;
    margin-bottom: 6px;
  }
`;

const ToolName = styled.div`
  font-weight: 600;
  color: var(--foreground-default);
  margin-bottom: 8px;
  font-size: 13px;
`;

const Section = styled.div`
  margin-bottom: 8px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.div`
  font-weight: 500;
  color: var(--foreground-muted);
  font-size: 12px;
  margin-bottom: 4px;
`;

const CodeBlock = styled.pre`
  background: var(--background-muted);
  border: 1px solid var(--border-default);
  border-radius: 4px;
  padding: 8px;
  font-size: 12px;
  color: var(--foreground-default);
  overflow-x: hidden;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 300px;
  overflow-y: auto;
  max-width: 100%;
  
  @media (max-width: 768px) {
    font-size: 11px;
    padding: 6px;
    max-height: 200px;
  }
`;

const ScreenshotImage = styled.img`
  max-width: 100%;
  height: auto;
  border-radius: 4px;
  border: 1px solid var(--border-default);
  margin-top: 8px;
  display: block;
  cursor: pointer;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.9;
  }
`;

const ImageModal = styled.div<{ open: boolean }>`
  display: ${props => props.open ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 10000;
  align-items: center;
  justify-content: center;
  padding: 20px;
  cursor: pointer;
`;

const ModalImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 8px;
`;

const ImageContainer = styled.div`
  position: relative;
`;

const SuccessIndicator = styled.span<{ success?: boolean }>`
  color: ${props => props.success ? 'var(--success-default)' : 'var(--danger-default)'};
  font-weight: 600;
`;

// Lazy-loaded screenshot component with intersection observer
function LazyScreenshot({ 
  screenshotUrl, 
  isExpanded 
}: { 
  screenshotUrl: string
  isExpanded: boolean 
}) {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const unloadTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    // Only observe if expanded
    if (!isExpanded) {
      // Use setTimeout to avoid synchronous setState in effect
      setTimeout(() => {
        setImageSrc(null);
        hasLoadedRef.current = false;
      }, 0);
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      if (unloadTimeoutRef.current) {
        clearTimeout(unloadTimeoutRef.current);
        unloadTimeoutRef.current = null;
      }
      return;
    }

    // Always keep image loaded if modal is open
    if (modalOpen) {
      // Use setTimeout to avoid synchronous setState in effect
      setTimeout(() => {
        setImageSrc(screenshotUrl);
      }, 0);
      if (unloadTimeoutRef.current) {
        clearTimeout(unloadTimeoutRef.current);
        unloadTimeoutRef.current = null;
      }
      return;
    }

    // Create observer with stable settings
    if (!observerRef.current && containerRef.current) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio > 0.1) {
              // Clear any pending unload
              if (unloadTimeoutRef.current) {
                clearTimeout(unloadTimeoutRef.current);
                unloadTimeoutRef.current = null;
              }
              
              // Load image when visible
              if (!imageSrc) {
                setImageSrc(screenshotUrl);
                hasLoadedRef.current = true;
              }
            } else if (hasLoadedRef.current && !modalOpen) {
              // Only unload if we've loaded before and not in modal
              // Add delay to prevent rapid toggling
              if (unloadTimeoutRef.current) {
                clearTimeout(unloadTimeoutRef.current);
              }
              unloadTimeoutRef.current = setTimeout(() => {
                if (!modalOpen) {
                  setImageSrc(null);
                  hasLoadedRef.current = false;
                }
              }, 1000); // 1 second delay before unloading
            }
          });
        },
        {
          rootMargin: '200px', // Large margin to prevent rapid toggling
          threshold: 0.1
        }
      );

      observerRef.current.observe(containerRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      if (unloadTimeoutRef.current) {
        clearTimeout(unloadTimeoutRef.current);
        unloadTimeoutRef.current = null;
      }
    };
  }, [isExpanded, screenshotUrl, modalOpen, imageSrc]);

  // Keep image loaded if modal is open
  useEffect(() => {
    if (modalOpen && !imageSrc) {
      // Use setTimeout to avoid synchronous setState in effect
      setTimeout(() => {
        setImageSrc(screenshotUrl);
        hasLoadedRef.current = true;
      }, 0);
    }
  }, [modalOpen, screenshotUrl, imageSrc]);

  return (
    <>
      <ImageContainer ref={containerRef}>
        {imageSrc ? (
          <ScreenshotImage
            src={imageSrc}
            alt="Screenshot"
            onClick={() => setModalOpen(true)}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <div style={{
            width: '100%',
            height: '200px',
            background: 'var(--background-muted)',
            border: '1px solid var(--border-default)',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--foreground-muted)',
            fontSize: '12px'
          }}>
            Image will load when visible...
          </div>
        )}
      </ImageContainer>
      
      <ImageModal open={modalOpen} onClick={() => setModalOpen(false)}>
        <ModalImage
          src={screenshotUrl}
          alt="Screenshot (enlarged)"
          onClick={(e) => e.stopPropagation()}
        />
      </ImageModal>
    </>
  );
}

function toolCallHasError(tc: ToolCall): boolean {
  return typeof tc.error === 'string' && tc.error.trim() !== '';
}

export function ToolCallMessage({ toolCalls, content }: ToolCallMessageProps) {
  const [expanded, setExpanded] = useState(() => toolCalls.some(toolCallHasError));

  const hasResults = toolCalls.some(tc => Boolean(tc.result));
  const hasErrors = toolCalls.some(toolCallHasError);
  const showOutcomeStatus = hasResults || hasErrors;
  const allSuccessful = toolCalls.every(tc => !toolCallHasError(tc) && tc.success !== false);
  
  return (
    <Container>
      <Header onClick={() => setExpanded(!expanded)}>
        <Title>{content}</Title>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {showOutcomeStatus && (
            <Status success={allSuccessful}>
              {allSuccessful ? '✓ Completed' : '✗ Failed'}
            </Status>
          )}
          <ExpandIcon expanded={expanded}>▼</ExpandIcon>
        </div>
      </Header>
      
      <Details expanded={expanded}>
        {toolCalls.map((toolCall, index) => (
          <ToolCallItem key={toolCall.id || index}>
            <ToolName>
              {toolCall.name}
              {toolCall.success !== undefined && (
                <SuccessIndicator success={toolCall.success}>
                  {' '}{toolCall.success ? '✓' : '✗'}
                </SuccessIndicator>
              )}
            </ToolName>
            
            {toolCall.id && (
              <Section>
                <SectionTitle>Call ID:</SectionTitle>
                <CodeBlock>
                  {toolCall.id}
                </CodeBlock>
              </Section>
            )}
            
            {/* Show Input: from arguments (server tools) */}
            {toolCall.arguments !== undefined && toolCall.arguments !== null && Object.keys(toolCall.arguments).length > 0 && (
              <Section>
                <SectionTitle>Input:</SectionTitle>
                <CodeBlock>
                  {(() => {
                    // Sanitize for display only - data remains unchanged in memory
                    // Use a more robust regex that handles very long base64 strings
                    const sanitizeBase64 = (str: string): string => {
                      // Match data:image/...;base64,... where base64 part is at least 50 chars
                      // Handle very long base64 strings that might be truncated in display
                      // Use a more permissive pattern that matches even if base64 is very long
                      const base64Pattern = /data:image\/[^;]+;base64,[A-Za-z0-9+/=\s]{50,}(?:[A-Za-z0-9+/=\s]*)?/g;
                      let sanitized = str.replace(base64Pattern, '[Screenshot Image]');
                      
                      // Also check for truncated base64 (if it ends with base64 but is cut off)
                      // This handles cases where the base64 string is extremely long and might be displayed partially
                      if (sanitized.includes('base64,') && sanitized.length > 1000) {
                        // If string is very long and contains base64, try a more aggressive replacement
                        const truncatedPattern = /data:image\/[^;]+;base64,[A-Za-z0-9+/=\s]{20,}/g;
                        sanitized = sanitized.replace(truncatedPattern, '[Screenshot Image]');
                      }
                      
                      return sanitized;
                    };
                    
                    const sanitizeForDisplay = (obj: unknown): unknown => {
                      if (typeof obj === 'string') {
                        return sanitizeBase64(obj);
                      }
                      if (Array.isArray(obj)) {
                        return obj.map(sanitizeForDisplay);
                      }
                      if (obj && typeof obj === 'object') {
                        const sanitized: Record<string, unknown> = {};
                        for (const [key, value] of Object.entries(obj)) {
                          if (typeof value === 'string') {
                            // Check if it's a base64 data URL (even partial matches)
                            if (value.includes('data:image/') && value.includes('base64,')) {
                              sanitized[key] = '[Screenshot Image]';
                            } else {
                              // Check if string contains embedded base64
                              sanitized[key] = sanitizeBase64(value);
                            }
                          } else {
                            sanitized[key] = sanitizeForDisplay(value);
                          }
                        }
                        return sanitized;
                      }
                      return obj;
                    };
                    
                    try {
                      // Handle arguments that might be a string or object
                      let argsObj = toolCall.arguments;
                      
                      // Convert to string first to sanitize, then parse
                      let argsStr = typeof argsObj === 'string' 
                        ? argsObj 
                        : JSON.stringify(argsObj);
                      
                      // Sanitize base64 in the string BEFORE any parsing
                      argsStr = sanitizeBase64(argsStr);
                      
                      // Now try to parse the sanitized string
                      try {
                        argsObj = JSON.parse(argsStr);
                      } catch {
                        // If parsing fails, return the sanitized string (might be malformed JSON)
                        return argsStr;
                      }
                      
                      // Sanitize the parsed object (deep sanitization)
                      const sanitized = sanitizeForDisplay(argsObj);
                      const jsonStr = JSON.stringify(sanitized, null, 2);
                      
                      // Final pass: sanitize any base64 that might have been in the JSON string itself
                      return sanitizeBase64(jsonStr);
                    } catch {
                      // If everything fails, sanitize as string
                      const argsStr = typeof toolCall.arguments === 'string' 
                        ? toolCall.arguments 
                        : JSON.stringify(toolCall.arguments);
                      return sanitizeBase64(argsStr);
                    }
                  })()}
                </CodeBlock>
              </Section>
            )}
            
            {/* Show Result: always show if there's a result */}
            {toolCall.result && (
              <Section>
                <SectionTitle>Result:</SectionTitle>
                {(() => {
                  try {
                    const resultData = JSON.parse(toolCall.result);
                    // Check if result contains a screenshot data URL
                    if (resultData.screenshot && typeof resultData.screenshot === 'string' && resultData.screenshot.startsWith('data:image/')) {
                      // Create a sanitized version for display (without the base64 data)
                      const sanitizedData = { ...resultData };
                      sanitizedData.screenshot = '[Screenshot Image - see below]';
                      return (
                        <>
                          <CodeBlock>
                            {JSON.stringify(sanitizedData, null, 2)}
                          </CodeBlock>
                          <LazyScreenshot 
                            screenshotUrl={resultData.screenshot}
                            isExpanded={expanded}
                          />
                        </>
                      );
                    }
                    // Regular JSON result
                    return (
                      <CodeBlock>
                        {toolCall.result}
                      </CodeBlock>
                    );
                  } catch {
                    // Not JSON, display as-is
                    return (
                      <CodeBlock>
                        {toolCall.result}
                      </CodeBlock>
                    );
                  }
                })()}
              </Section>
            )}
            
            {toolCall.error && (
              <Section>
                <SectionTitle>Error:</SectionTitle>
                <CodeBlock style={{ color: 'var(--danger-default)' }}>
                  {toolCall.error}
                </CodeBlock>
              </Section>
            )}
          </ToolCallItem>
        ))}
      </Details>
    </Container>
  );
}
